/**
 * Type guards for message parts.
 *
 * Two different "ToolCallPart" types exist in the codebase:
 *
 * 1. **AI SDK's `ToolCallPart`** (from `ai` package):
 *    - Used for MODEL messages (sent to/from LLM)
 *    - Has `input` property for tool arguments
 *    - Imported and used in AtlasChatTransport.ts
 *
 * 2. **Thread message parts** (assistant-ui structure):
 *    - Used for UI THREAD messages (displayed to user)
 *    - Has `args`, `result`, `status` properties
 *    - Used in AtlasAssistant.tsx, extract.ts, types.ts
 *
 * This module provides guards for both. The AI SDK guards use the AI SDK types directly.
 * The thread guards use our local `ThreadToolCallPart` interface.
 */
import type { ToolCallPart as ModelToolCallPart, TextPart, ToolResultPart } from 'ai';

/**
 * Thread message part representing a tool call.
 * Matches assistant-ui's ToolCallContentPart structure.
 *
 * Different from AI SDK's ToolCallPart which has `input` instead of `args/result/status`.
 */
export interface ThreadToolCallPart {
  type: 'tool-call';
  toolName: string;
  args?: Record<string, unknown>;
  result?: Record<string, unknown>;
  status?: { type: string };
}

/**
 * Message with parts array - matches assistant-ui's ThreadMessage structure.
 * Used for extracting tool outputs from thread history.
 */
export interface MessageWithParts {
  parts: ReadonlyArray<{ type: string }>;
  createdAt?: Date;
}

/**
 * Type guard for AI SDK's ToolCallPart (model message format).
 * Use for messages being sent to/from the LLM.
 */
export function isModelToolCallPart(part: unknown): part is ModelToolCallPart {
  return typeof part === 'object' && part !== null && 'type' in part && part.type === 'tool-call';
}

/**
 * Type guard for AI SDK's TextPart (model message format).
 */
export function isTextPart(part: unknown): part is TextPart {
  return typeof part === 'object' && part !== null && 'type' in part && part.type === 'text';
}

/**
 * Type guard for AI SDK's ToolResultPart (model message format).
 */
export function isToolResultPart(part: unknown): part is ToolResultPart {
  return typeof part === 'object' && part !== null && 'type' in part && part.type === 'tool-result';
}

/**
 * Type guard for thread tool call parts (UI message format).
 * Use for messages displayed in the chat UI thread.
 *
 * Note: This uses the same discriminant as AI SDK ('tool-call') but preserves
 * the original type with ThreadToolCallPart properties intersected in.
 * This allows the guard to work with assistant-ui's MessagePartClientState
 * which has additional properties beyond our minimal interface.
 */
export function isThreadToolCallPart<T extends { type: string }>(part: T): part is T & ThreadToolCallPart {
  return part.type === 'tool-call';
}

/**
 * Type guard for messages with parts array.
 * Filters generic ThreadMessage[] to MessageWithParts[] safely.
 */
export function isMessageWithParts(message: unknown): message is MessageWithParts {
  return (
    typeof message === 'object' &&
    message !== null &&
    'parts' in message &&
    Array.isArray((message as { parts: unknown }).parts)
  );
}
